/**
 * Control Room — the default view (§7.1, view 1).
 *
 * Hero: the cross-section. Right: the gauge rail and alarm summary. Bottom: control strips for
 * units, spillway gates, low-level outlets and the plant mode selector.
 */

import { useMemo } from 'react';
import { useSession } from '../../store/session';
import { useDispatch } from '../useEngine';
import { getSite } from '../../sites';
import { getScenario } from '../../scenarios';
import { CrossSection } from '../widgets/CrossSection';
import { Readout, type Tone } from '../widgets/Readout';
import { JogButtons } from '../widgets/Jog';
import { useConfirmed } from '../widgets/Confirm';
import { Gloss } from '../glossary/GlossaryDrawer';
import { elevation, flow, money, percent, power, price } from '../format';
import type { PlantMode } from '../../engine/types';
import { CHECKLIST_ITEMS } from '../../engine/actions';

export function ControlRoom(): JSX.Element {
  const snap = useSession((s) => s.snapshot);
  const siteId = useSession((s) => s.siteId);
  const scenarioId = useSession((s) => s.scenarioId);
  const settings = useSession((s) => s.settings);
  const selectedGate = useSession((s) => s.selectedGate);
  const selectedUnit = useSession((s) => s.selectedUnit);
  const selectGate = useSession((s) => s.selectGate);
  const selectUnit = useSession((s) => s.selectUnit);
  const setView = useSession((s) => s.setView);
  const dispatch = useDispatch();
  const confirmed = useConfirmed();
  const site = getSite(siteId);
  const scenario = getScenario(scenarioId);
  const activeSite = useSession((s) => s.activeSite);
  const primarySnapshot = useSession((s) => s.primarySnapshot);
  const secondarySnapshot = useSession((s) => s.secondarySnapshot);

  // Cascade panel data, from the two snapshots the worker already sends.
  const cascade = useMemo(() => {
    if (!primarySnapshot || !secondarySnapshot) return null;
    const link = getScenario(primarySnapshot.scenarioId ?? scenarioId).cascade;
    const other = activeSite === 'primary' ? secondarySnapshot : primarySnapshot;
    return {
      label: activeSite === 'primary' ? 'downstream plant' : 'upstream plant',
      otherHeadwater: other.headwater,
      arriving: secondarySnapshot.upstreamArriving,
      inTransit: secondarySnapshot.upstreamInTransit,
      lagH: link?.lagH ?? 14,
    };
  }, [primarySnapshot, secondarySnapshot, activeSite, scenarioId]);

  const tones = useMemo(() => {
    if (!snap) return null;
    const freeboard = site.dam.crest - snap.headwater;
    const hwTone: Tone =
      snap.headwater > site.dam.mfl
        ? 'alarm'
        : snap.headwater > site.dam.fsl
          ? 'warn'
          : snap.headwater < site.dam.mol
            ? 'alarm'
            : 'normal';
    const tdgTone: Tone = snap.tdgTailrace > snap.tdgLimit ? 'alarm' : snap.tdgTailrace > snap.tdgLimit - 3 ? 'warn' : 'normal';
    const gaugeTone: Tone =
      snap.gaugeFlow >= site.downstream.floodModerate
        ? 'alarm'
        : snap.gaugeFlow >= site.downstream.floodMinor
          ? 'warn'
          : 'normal';
    const reserveTone: Tone = snap.reserveMW < snap.reserveObligationMW ? 'warn' : 'ok';
    const minFlowTone: Tone = snap.totalOutflow < snap.minimumFlow ? 'alarm' : 'normal';
    return { freeboard, hwTone, tdgTone, gaugeTone, reserveTone, minFlowTone };
  }, [snap, site]);

  if (!snap || !tones) {
    return <div style={{ padding: 'var(--s5)', color: 'var(--ink-dim)' }}>Initialising engine…</div>;
  }

  const setMode = (mode: PlantMode): void => {
    confirmed(`Change plant mode to ${mode}`, 'Plant-mode changes are critical actions.', () =>
      dispatch({ t: 'plant-mode', mode }),
    );
  };

  return (
    <div className="cr">
      <div className="cr-left">
        {/* Hero */}
        <div className="cr-hero">
          <CrossSection
            site={site}
            snap={snap}
            reducedMotion={settings.reducedMotion}
            onSelectGate={selectGate}
            onSelectUnit={selectUnit}
            selectedGate={selectedGate}
            selectedUnit={selectedUnit}
          />
        </div>

        {/* Control strips */}
        <div className="cr-strips">
          {/* Units */}
          <section className="panel" aria-label="Units">
            <div className="label" style={{ marginBottom: 6 }}>
              Units
            </div>
            <div style={{ display: 'grid', gap: 5 }}>
              {snap.units.map((u, i) => {
                const online = u.mode === 'online';
                const colour =
                  u.mode === 'tripped' ? 'var(--alarm)' : online ? 'var(--ink)' : 'var(--ink-dim)';
                return (
                  <div
                    key={u.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '18px 62px 1fr auto',
                      gap: 6,
                      alignItems: 'center',
                      borderLeft: `2px solid ${selectedUnit === i ? 'var(--water-shallow)' : 'transparent'}`,
                      paddingLeft: 4,
                    }}
                    onClick={() => selectUnit(i)}
                  >
                    <span className="num" style={{ color: 'var(--ink-dim)' }}>
                      {i + 1}
                    </span>
                    <span className="num" style={{ color: colour, fontSize: 11, textTransform: 'uppercase' }}>
                      {u.mode === 'starting' ? `start ${Math.ceil(u.phaseS)}s` : u.mode}
                    </span>
                    <span className="num" style={{ color: colour }}>
                      {online ? `${Math.round(u.mw)} MW` : '—'}
                      {online && (
                        <span className="unit">
                          {' '}
                          {Math.round(u.q)} m³/s · {(u.wicket * 100).toFixed(0)}%
                        </span>
                      )}
                    </span>
                    <span style={{ display: 'flex', gap: 3 }}>
                      {online ? (
                        <>
                          <JogButtons
                            label={`unit ${i + 1} load`}
                            step={10}
                            ariaLabel={`Unit ${i + 1} load jog`}
                            onJog={(d) =>
                              dispatch({ t: 'unit-setpoint', unit: i, mw: Math.max(0, u.setpointMW + d) })
                            }
                          />
                          <button
                            onClick={() =>
                              confirmed(`Stop unit ${i + 1}`, 'The unit will unload and come off line.', () =>
                                dispatch({ t: 'unit-stop', unit: i }),
                              )
                            }
                            data-testid={`unit-stop-${i}`}
                          >
                            Stop
                          </button>
                        </>
                      ) : (
                        <button
                          disabled={u.mode !== 'offline'}
                          onClick={() =>
                            confirmed(
                              `Start unit ${i + 1}`,
                              `Intake gate, speed no load, sync — about 5.5 minutes and $${site.powerhouse.startCost.toLocaleString('en-US')}.`,
                              () => dispatch({ t: 'unit-start', unit: i }),
                            )
                          }
                          data-testid={`unit-start-${i}`}
                        >
                          Start
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Spillway gates */}
          {site.spillway.kind === 'gated' && (
            <section className="panel" aria-label="Spillway gates">
              <div className="label" style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                <span>
                  <Gloss id="tainter-gate">Spillway gates</Gloss>
                </span>
                <span className="num" style={{ textTransform: 'none' }}>
                  {Math.round(snap.spillFlow).toLocaleString('en-US')} m³/s
                </span>
              </div>
              <div className="scroll" style={{ display: 'grid', gap: 4, maxHeight: 132 }}>
                {snap.gates.map((g, i) => (
                  <GateRow
                    key={g.id}
                    index={i}
                    opening={g.opening}
                    target={g.target}
                    q={g.q}
                    hoist={g.hoist}
                    heaterOn={g.heaterOn}
                    heaterMinutes={g.heaterMinutes}
                    maxOpening={site.spillway.gateHeight}
                    selected={selectedGate === i}
                    onSelect={() => selectGate(i)}
                    onTarget={(v) => {
                      const delta = Math.abs(v - g.opening);
                      if (delta > 1.0) {
                        confirmed(
                          `Gate ${i + 1} to ${v.toFixed(2)} m`,
                          `A change of ${delta.toFixed(2)} m. At ${site.spillway.hoistRate} m/min this takes about ${Math.round(delta / site.spillway.hoistRate)} minutes.`,
                          () => dispatch({ t: 'gate-target', gate: i, opening: v }),
                        );
                      } else {
                        dispatch({ t: 'gate-target', gate: i, opening: v });
                      }
                    }}
                    onHeater={(on) => dispatch({ t: 'gate-heater', gate: i, on })}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Low-level outlets + plant mode */}
          <section className="panel" aria-label="Outlets and plant mode">
            <div className="label" style={{ marginBottom: 6 }}>
              <Gloss id="llo">Low-level outlets</Gloss>
            </div>
            {snap.llos.map((l, i) => (
              <div key={l.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <span className="num" style={{ color: 'var(--ink-dim)', width: 16 }}>
                  {i + 1}
                </span>
                <span className="num" style={{ width: 96 }}>
                  {(l.openFrac * 100).toFixed(0)}%<span className="unit">{Math.round(l.q)} m³/s</span>
                </span>
                <button
                  onClick={() =>
                    confirmed(
                      `${l.targetFrac > 0 ? 'Close' : 'Open'} low-level outlet ${i + 1}`,
                      l.targetFrac > 0
                        ? 'The outlet will travel shut.'
                        : `Full open passes about ${Math.round(site.llo.cd * site.llo.width * site.llo.height * Math.sqrt(2 * 9.81 * Math.max(0, snap.headwater - site.llo.centreline)))} m³/s at ${site.llo.tdgPercent}% TDG. Partial openings between ${site.llo.cavitationMinFrac * 100}% and ${site.llo.cavitationMaxFrac * 100}% cavitate after two hours.`,
                      () => dispatch({ t: 'llo-target', llo: i, frac: l.targetFrac > 0 ? 0 : 1 }),
                    )
                  }
                >
                  {l.targetFrac > 0 ? 'Close' : 'Open'}
                </button>
                {l.cavitationMinutes > 0 && (
                  <span style={{ color: 'var(--warn)', fontSize: 11 }} className="num">
                    ▲ cavitation {Math.round(l.cavitationMinutes)} min
                  </span>
                )}
              </div>
            ))}

            <div className="label" style={{ margin: '10px 0 6px' }}>
              Plant mode
            </div>
            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {(['manual', 'level-hold', 'discharge-hold', 'schedule', 'agc'] as PlantMode[]).map((m) => (
                <button
                  key={m}
                  className={snap.control.mode === m ? 'primary' : undefined}
                  onClick={() => setMode(m)}
                  data-testid={`mode-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            {snap.control.mode === 'level-hold' && (
              <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="num" style={{ color: 'var(--ink-dim)' }}>
                  target {snap.control.levelTarget.toFixed(2)} m
                </span>
                <JogButtons
                  label="level target"
                  step={0.25}
                  ariaLabel="Level target jog"
                  onJog={(d) =>
                    dispatch({
                      t: 'level-target',
                      elevation: snap.control.levelTarget + d,
                      tolerance: snap.control.levelTolerance,
                    })
                  }
                />
                <button
                  className={snap.control.autoSpillArmed ? 'armed' : undefined}
                  onClick={() =>
                    dispatch({
                      t: 'auto-spill',
                      armed: !snap.control.autoSpillArmed,
                      maxOpening: snap.control.autoSpillMaxOpening,
                    })
                  }
                  title="Level hold will not open a gate unless auto-spill is armed."
                >
                  auto-spill {snap.control.autoSpillArmed ? 'armed' : 'off'}
                </button>
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ── Gauge rail ───────────────────────────────────────────────────────── */}
      <aside className="cr-rail" aria-label="Gauges">
        <div style={{ display: 'grid', gap: 'var(--s3)' }}>
          <Readout
            label="Headwater"
            value={elevation(snap.headwater, settings).value}
            unit={elevation(snap.headwater, settings).unit}
            tone={tones.hwTone}
            hint={`Transducer ${snap.selectedTransducer.toUpperCase()}. A ${snap.headwaterA.toFixed(2)} · B ${snap.headwaterB.toFixed(2)}`}
            onClick={() => setView('reservoir')}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s2)' }}>
            <Readout
              label="Freeboard"
              value={tones.freeboard.toFixed(2)}
              unit="m"
              size="sm"
              tone={tones.freeboard < 2 ? 'alarm' : tones.freeboard < 5 ? 'warn' : 'normal'}
            />
            <Readout
              label="Rule curve"
              value={snap.ruleCurveTarget.toFixed(1)}
              unit="m"
              size="sm"
              tone={
                snap.headwater > snap.ruleCurveUpper || snap.headwater < snap.ruleCurveLower ? 'warn' : 'ok'
              }
            />
            <Readout label="Tailwater" value={snap.tailwater.toFixed(2)} unit="m" size="sm" />
            <Readout label="Gross head" value={snap.grossHead.toFixed(1)} unit="m" size="sm" />
          </div>

          <hr style={{ border: 0, borderTop: '1px solid var(--line-soft)' }} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s2)' }}>
            <Readout
              label="Inflow (gauged)"
              value={flow(snap.inflowGauged, settings).value}
              unit={flow(snap.inflowGauged, settings).unit}
              size="sm"
            />
            <Readout
              label="Inflow (computed)"
              value={flow(snap.inflowComputed, settings).value}
              unit={flow(snap.inflowComputed, settings).unit}
              size="sm"
              tone={
                // The estimator differences a two-hour window, so it needs two hours before its
                // divergence means anything. Flagging it earlier is a false positive every run.
                snap.simMinute > 120 &&
                Math.abs(snap.inflowComputed - snap.inflowGauged) > snap.inflowGauged * 0.3
                  ? 'warn'
                  : 'dim'
              }
              hint={
                snap.simMinute > 120
                  ? 'Back-calculated from the level sensor by mass balance. If it diverges from the gauged value while gates and units are steady, suspect the level transducer.'
                  : 'Back-calculated from the level sensor by mass balance. The estimate differences a two-hour window, so it is still settling.'
              }
            />
            <Readout
              label="Total release"
              value={flow(snap.totalOutflow, settings).value}
              unit={flow(snap.totalOutflow, settings).unit}
              size="sm"
              tone={tones.minFlowTone}
              hint={`Licence minimum ${snap.minimumFlow} m³/s`}
            />
            <Readout
              label="Spill"
              value={flow(snap.spillFlow, settings).value}
              unit={flow(snap.spillFlow, settings).unit}
              size="sm"
              tone={snap.spillFlow > 0 ? 'warn' : 'dim'}
            />
          </div>

          <hr style={{ border: 0, borderTop: '1px solid var(--line-soft)' }} />

          <Readout label="Generation" value={power(snap.exportMW).value} unit="MW" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s2)' }}>
            <Readout
              label="Export limit"
              value={power(snap.exportLimitMW).value}
              unit="MW"
              size="sm"
              tone={snap.exportLimitMW < site.grid.exportLimitMW ? 'warn' : 'dim'}
            />
            <Readout
              label="Reserve"
              value={power(snap.reserveMW).value}
              unit={`/ ${snap.reserveObligationMW} MW`}
              size="sm"
              tone={tones.reserveTone}
            />
            <Readout label="Price" value={price(snap.price).value} unit="/MWh" size="sm" />
            <Readout
              label="Water value"
              value={price(snap.waterValue).value}
              unit="/MWh"
              size="sm"
              hint="What the next MWh of stored water is worth. Sell when the price beats it."
            />
            <Readout
              label="TDG tailrace"
              value={percent(snap.tdgTailrace).value}
              unit="%"
              size="sm"
              tone={tones.tdgTone}
              hint={`Limit ${snap.tdgLimit}%${snap.tdgWaiverGranted ? ' (waiver in force)' : ''}`}
            />
            <Readout
              label={site.downstream.gaugeName}
              value={flow(snap.gaugeFlow, settings).value}
              unit={flow(snap.gaugeFlow, settings).unit}
              size="sm"
              tone={tones.gaugeTone}
              onClick={() => setView('river')}
            />
          </div>

          <hr style={{ border: 0, borderTop: '1px solid var(--line-soft)' }} />

          <div>
            <div className="label" style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
              <span>Alarms</span>
              <button style={{ padding: '1px 6px', fontSize: 11 }} onClick={() => dispatch({ t: 'alarm-ack-all' })}>
                Ack all
              </button>
            </div>
            {snap.alarms.length === 0 && <div style={{ color: 'var(--ink-dim)' }}>No active alarms.</div>}
            <div style={{ display: 'grid', gap: 4 }}>
              {snap.alarms.slice(0, 6).map((a) => (
                <button
                  key={a.id}
                  onClick={() => setView('alarms')}
                  style={{
                    textAlign: 'left',
                    background: 'var(--surface-2)',
                    borderLeft: `3px solid ${a.priority === 'emergency' || a.priority === 'high' ? 'var(--alarm)' : a.priority === 'medium' ? 'var(--warn)' : 'var(--ink-dim)'}`,
                    padding: '4px 6px',
                  }}
                >
                  <div style={{ fontSize: 11, color: 'var(--ink-dim)', textTransform: 'uppercase' }}>
                    {a.priority}
                    {a.status === 'unacked-active' ? ' · unacked' : ''}
                  </div>
                  <div>{a.title}</div>
                </button>
              ))}
            </div>
          </div>

          {cascade && (
            <div className="panel" style={{ borderColor: 'var(--water-shallow)' }} data-testid="cascade-strip">
              <div className="label" style={{ marginBottom: 6 }}>
                Cascade — {cascade.label}
              </div>
              <div style={{ display: 'grid', gap: 4, fontSize: 12 }} className="num">
                <Line k="Their forebay" v={`${cascade.otherHeadwater.toFixed(2)} m`} />
                <Line k="Arriving from upstream" v={`${Math.round(cascade.arriving).toLocaleString('en-US')} m³/s`} />
                <Line k="In transit" v={`${(cascade.inTransit / 1e6).toFixed(1)} ×10⁶ m³`} />
                <Line k="Link lag" v={`${cascade.lagH} h`} />
              </div>
              <div style={{ color: 'var(--ink-dim)', fontSize: 11, marginTop: 6 }}>
                {activeSite === 'primary'
                  ? 'What you release now reaches them in ' + cascade.lagH + ' hours. Water already in transit cannot be recalled.'
                  : 'Your inflow is their decision, made ' + cascade.lagH + ' hours ago. Watch the upstream release, not your forebay.'}
              </div>
            </div>
          )}

          {scenario.tutorial === true && (
            <div className="panel" style={{ borderColor: 'var(--water-shallow)' }} data-testid="checklist">
              <div className="label" style={{ marginBottom: 6 }}>
                Handover checklist — {snap.checklist.length} of {CHECKLIST_ITEMS.length}
              </div>
              <div style={{ display: 'grid', gap: 3 }}>
                {CHECKLIST_ITEMS.map((item) => {
                  const done = snap.checklist.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      data-checklist-item={item.id}
                      data-done={done}
                      style={{ display: 'flex', gap: 6, color: done ? 'var(--ok)' : 'var(--ink-dim)' }}
                    >
                      <span aria-hidden="true">{done ? '✓' : '○'}</span>
                      <span>{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>
            <div className="label">Shift</div>
            <div className="num">
              {money(snap.net)} net · {money(snap.penalties)} penalties
            </div>
            <div className="num">{scenario.teaches}</div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Line({ k, v }: { k: string; v: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--ink-dim)' }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

function GateRow({
  index,
  opening,
  target,
  q,
  hoist,
  heaterOn,
  heaterMinutes,
  maxOpening,
  selected,
  onSelect,
  onTarget,
  onHeater,
}: {
  index: number;
  opening: number;
  target: number;
  q: number;
  hoist: string;
  heaterOn: boolean;
  heaterMinutes: number;
  maxOpening: number;
  selected: boolean;
  onSelect(): void;
  onTarget(v: number): void;
  onHeater(on: boolean): void;
}): JSX.Element {
  const moving = Math.abs(target - opening) > 0.005;
  return (
    <div
      onClick={onSelect}
      style={{
        display: 'grid',
        gridTemplateColumns: '16px 92px 1fr auto auto',
        gap: 5,
        alignItems: 'center',
        borderLeft: `2px solid ${selected ? 'var(--water-shallow)' : 'transparent'}`,
        paddingLeft: 3,
      }}
    >
      <span className="num" style={{ color: 'var(--ink-dim)' }}>
        {index + 1}
      </span>
      <span className="num" style={{ color: hoist !== 'ok' ? 'var(--alarm)' : 'var(--ink)' }}>
        {opening.toFixed(2)}
        <span className="unit">m</span>
        {moving && <span style={{ color: 'var(--water-moving)' }}> →{target.toFixed(2)}</span>}
      </span>
      <span className="num" style={{ color: 'var(--ink-dim)' }}>
        {q > 1 ? `${Math.round(q).toLocaleString('en-US')} m³/s` : hoist !== 'ok' ? hoist.toUpperCase() : '—'}
      </span>
      <JogButtons
        label={`gate ${index + 1}`}
        step={0.25}
        ariaLabel={`Gate ${index + 1} jog`}
        disabled={hoist !== 'ok'}
        onJog={(d) => onTarget(Math.max(0, Math.min(maxOpening, target + d)))}
      />
      <button
        onClick={(e) => {
          e.stopPropagation();
          onHeater(!heaterOn);
        }}
        className={heaterOn ? 'armed' : undefined}
        title={heaterOn ? `Heater on for ${Math.round(heaterMinutes)} min` : 'Gate heater off'}
        style={{ padding: '2px 6px', fontSize: 11 }}
      >
        ♨
      </button>
    </div>
  );
}
